import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  bookingApi,
  unwrap,
  type ApiEnvelope,
} from '@/lib/api/client';

/**
 * The three supporting records a booking is built out of: where it happens,
 * who runs it, and what kind of thing it is.
 *
 * All from docs/booking-api.swagger.json. Each is a flat list with no paging,
 * a create, an update and a delete — the update models take an `id` the create
 * models do not, which is the only difference between the two calls.
 */

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

// ------------------------------------------------------------------ hosts

/** `HostDto`. */
export interface BookingHost {
  id: string;
  name: string | null;
  description: string | null;
  rating: number | null;
  avatar: string | null;
  email: string | null;
  phoneNumber: string | null;
  firstName: string | null;
  lastName: string | null;
}

export function useHosts() {
  return useQuery({
    queryKey: ['booking-setup', 'hosts'],
    queryFn: () => get<BookingHost[]>('hosts'),
  });
}

function useInvalidate(key: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['booking-setup', key] });
    // The pickers on the events and booking pages read the same lists.
    void queryClient.invalidateQueries({ queryKey: ['booking'] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };
}

/** `CreateEventHostBindingModel` — a host is a user, plus a description. */
export function useCreateHost() {
  const invalidate = useInvalidate('hosts');

  return useMutation({
    mutationFn: (body: { userId: string; description: string }) =>
      unwrap<unknown>(bookingApi.post('hosts', body)),
    onSuccess: invalidate,
  });
}

/** `UpdateEventHostBindingModel`. The user behind a host cannot be changed. */
export function useUpdateHost() {
  const invalidate = useInvalidate('hosts');

  return useMutation({
    mutationFn: (body: { id: string; description: string; rating: number }) =>
      unwrap<unknown>(bookingApi.put('hosts', body)),
    onSuccess: invalidate,
  });
}

export function useDeleteHost() {
  const invalidate = useInvalidate('hosts');

  return useMutation({
    mutationFn: (hostId: string) =>
      unwrap<unknown>(bookingApi.delete(`hosts/${hostId}`)),
    onSuccess: invalidate,
  });
}

/** `POST users/search` on the main API — who a host is. */
export interface SearchedUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  completeName: string | null;
  email: string | null;
  avatar: string | null;
}

export function useUserSearch() {
  return useMutation({
    mutationFn: (searchText: string) =>
      unwrap<{ results: SearchedUser[] }>(
        api.post('users/search', { SearchText: searchText })
      ).then((data) => data.results ?? []),
  });
}

// -------------------------------------------------------------- locations

/** `LocationDto`. `name` is the internal one; customers see `nameEn`/`nameEs`. */
export interface BookingLocationRecord {
  id: string;
  name: string | null;
  nameEn: string | null;
  nameEs: string | null;
  description: string | null;
  descriptionEs: string | null;
  imageUrl: string | null;
}

export type LocationInput = Omit<BookingLocationRecord, 'id'>;

export function useLocations() {
  return useQuery({
    queryKey: ['booking-setup', 'locations'],
    queryFn: () => get<BookingLocationRecord[]>('locations'),
  });
}

export function useCreateLocation() {
  const invalidate = useInvalidate('locations');

  return useMutation({
    mutationFn: (body: LocationInput) =>
      unwrap<unknown>(bookingApi.post('locations', body)),
    onSuccess: invalidate,
  });
}

export function useUpdateLocation() {
  const invalidate = useInvalidate('locations');

  return useMutation({
    mutationFn: (body: LocationInput & { id: string }) =>
      unwrap<unknown>(bookingApi.put('locations', body)),
    onSuccess: invalidate,
  });
}

export function useDeleteLocation() {
  const invalidate = useInvalidate('locations');

  return useMutation({
    mutationFn: (locationId: string) =>
      unwrap<unknown>(bookingApi.delete(`locations/${locationId}`)),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------- service types

/** `ServiceTypeDto` — a name in each language, and nothing else. */
export interface ServiceTypeRecord {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
}

export function useServiceTypes() {
  return useQuery({
    queryKey: ['booking-setup', 'service-types'],
    // The list lives under /list; create, update and delete do not.
    queryFn: () => get<ServiceTypeRecord[]>('servicetypes/list'),
  });
}

export function useCreateServiceType() {
  const invalidate = useInvalidate('service-types');

  return useMutation({
    mutationFn: (body: { nameEn: string; nameEs: string }) =>
      unwrap<unknown>(bookingApi.post('servicetypes', body)),
    onSuccess: invalidate,
  });
}

export function useUpdateServiceType() {
  const invalidate = useInvalidate('service-types');

  return useMutation({
    mutationFn: (body: { id: string; nameEn: string; nameEs: string }) =>
      unwrap<unknown>(bookingApi.put('servicetypes', body)),
    onSuccess: invalidate,
  });
}

export function useDeleteServiceType() {
  const invalidate = useInvalidate('service-types');

  return useMutation({
    mutationFn: (serviceTypeId: string) =>
      unwrap<unknown>(bookingApi.delete(`servicetypes/${serviceTypeId}`)),
    onSuccess: invalidate,
  });
}
